import { useState } from 'react';
import { api, useApi, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Inspector, Loading, Sheet, Status, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';
import type { Asset } from './Library.tsx';

interface Campaign { id: string; name: string; status: string; revision: number; startsAt: string; endsAt: string; loopShareBp: number; priority: number; delivery: string; advertiserName: string; creativeCount: number; frequencyCapPerHour: number | null; rejectionReason: string | null }
interface InventoryScreen { screenName: string; capacitySeconds: number; committedSeconds: number; availableSeconds: number; deliverableSeconds: number; satisfiable: boolean; reasons: string[] }

export function Campaigns() {
  const org = useOrg();
  const { data, error, loading, reload } = useApi<{ campaigns: Campaign[] }>(orgPath(org, '/campaigns'), [org.id]);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <div className="page-head">
        <div><h1>Campaigns</h1><p>Paced advertising with approvals. Delivery is measured from player playback, never estimated audience.</p></div>
        {can(org, 'campaigns.write') && <button className="btn btn-primary" onClick={() => setCreating(true)}><Icon name="plus" /> New campaign</button>}
      </div>
      {error && <ErrorState error={error} retry={reload} />}
      {loading && !data && <Loading />}
      {data && !data.campaigns.length && <div className="card"><Empty title="No campaigns yet">Create a campaign, add creatives, and submit it for approval.</Empty></div>}
      {data && !!data.campaigns.length && (
        <div className="card table-wrap">
          <table className="table">
            <thead><tr><th>Campaign</th><th>Advertiser</th><th>Status</th><th>Flight</th><th>Share</th><th>Delivery</th></tr></thead>
            <tbody>
              {data.campaigns.map((c) => (
                <tr key={c.id}>
                  <td><button className="btn btn-ghost btn-sm" onClick={() => setOpen(c.id)}>{c.name}</button><div className="subtle">rev {c.revision} · {c.creativeCount} creative(s)</div></td>
                  <td>{c.advertiserName}</td>
                  <td><Status value={c.status} />{c.rejectionReason && <div className="subtle">{c.rejectionReason}</div>}</td>
                  <td className="subtle">{new Date(c.startsAt).toLocaleDateString()} – {new Date(c.endsAt).toLocaleDateString()}</td>
                  <td>{c.loopShareBp / 100}%{c.frequencyCapPerHour ? ` · ≤${c.frequencyCapPerHour}/h` : ''}</td>
                  <td><span className="badge plain">{c.delivery.replace('_', '-')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <CreateCampaign onClose={() => setCreating(false)} onCreated={(id) => { void reload(); setOpen(id); }} />}
      {open && <CampaignInspector id={open} onClose={() => setOpen(null)} onChanged={reload} />}
    </>
  );
}

function CreateCampaign({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const org = useOrg();
  const advertisers = useApi<{ advertisers: { id: string; name: string }[] }>(orgPath(org, '/advertisers'), [org.id]);
  const locations = useApi<{ locations: { id: string; name: string }[] }>(org.role === 'advertiser' ? null : orgPath(org, '/locations'), [org.id]);
  const day = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);
  const [f, setF] = useState({ name: '', advertiserId: '', start: day(0), end: day(14), share: 10, cap: '', spacing: 0, locationIds: [] as string[], category: '' });
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    try {
      const r = await api<{ id: string }>('POST', orgPath(org, '/campaigns'), {
        name: f.name, advertiserId: f.advertiserId || undefined, startsAt: new Date(f.start).toISOString(), endsAt: new Date(f.end).toISOString(),
        target: { locationIds: f.locationIds }, loopShareBp: Math.round(f.share * 100), frequencyCapPerHour: f.cap ? Number(f.cap) : null, minSpacingSeconds: f.spacing, category: f.category || null,
      });
      onCreated(r.id);
      onClose();
    } catch (e) {
      setErr((e as ApiFailure).message);
    }
  }
  return (
    <Sheet title="New campaign" onClose={onClose} footer={<button className="btn btn-primary" disabled={!f.name} onClick={() => void save()}>Create draft</button>}>
      <div className="stack" style={{ gap: 14 }}>
        {err && <div className="alert danger" role="alert">{err}</div>}
        <div className="field"><label htmlFor="c-name">Name</label><input id="c-name" className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        {org.role !== 'advertiser' && (
          <div className="field"><label htmlFor="c-adv">Advertiser</label><select id="c-adv" className="input" value={f.advertiserId} onChange={(e) => setF({ ...f, advertiserId: e.target.value })}><option value="">Choose…</option>{advertisers.data?.advertisers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
        )}
        <div className="row">
          <div className="field"><label htmlFor="c-s">Starts</label><input id="c-s" type="date" className="input" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></div>
          <div className="field"><label htmlFor="c-e">Ends</label><input id="c-e" type="date" className="input" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} /></div>
        </div>
        <div className="row">
          <div className="field"><label htmlFor="c-share">Share of airtime (%)</label><input id="c-share" type="number" min={1} max={100} className="input" value={f.share} onChange={(e) => setF({ ...f, share: Number(e.target.value) })} /></div>
          <div className="field"><label htmlFor="c-cap">Max plays per hour</label><input id="c-cap" type="number" min={1} className="input" value={f.cap} onChange={(e) => setF({ ...f, cap: e.target.value })} placeholder="No cap" /></div>
          <div className="field"><label htmlFor="c-sp">Min. spacing (s)</label><input id="c-sp" type="number" min={0} className="input" value={f.spacing} onChange={(e) => setF({ ...f, spacing: Number(e.target.value) })} /></div>
        </div>
        <div className="field"><label htmlFor="c-cat">Category (for competitor separation)</label><input id="c-cat" className="input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
        {locations.data && (
          <div className="field"><span className="label">Locations</span><div className="row">{locations.data.locations.map((l) => <label key={l.id} className="row" style={{ gap: 4 }}><input type="checkbox" checked={f.locationIds.includes(l.id)} onChange={(e) => setF({ ...f, locationIds: e.target.checked ? [...f.locationIds, l.id] : f.locationIds.filter((x) => x !== l.id) })} />{l.name}</label>)}</div></div>
        )}
      </div>
    </Sheet>
  );
}

function CampaignInspector({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const { data, error, reload } = useApi<{ campaign: { name: string; status: string; delivery: string }; creatives: { id: string; assetName: string; durationMs: number; status: string }[]; history: { revision: number; from: string; to: string; note: string | null; at: string }[]; inventory: { satisfiable: boolean; screens: InventoryScreen[] } }>(orgPath(org, `/campaigns/${id}`), [id]);
  const assets = useApi<{ assets: Asset[] }>(orgPath(org, '/assets?status=ready'), [org.id]);
  const delivery = useApi<{ observed: { started: number; completed: number; partial: number; skipped: number }; deliveredSeconds: number; shortfallSeconds: number | null; finalizationNote: string; note: string }>(orgPath(org, `/reports/campaigns/${id}`), [id]);
  const [adding, setAdding] = useState(false);
  const act = async (path: string, body: unknown, msg: string) => {
    try {
      await api('POST', orgPath(org, `/campaigns/${id}${path}`), body);
      toast(msg);
      void reload();
      onChanged();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  };
  const c = data?.campaign;
  return (
    <Inspector title={c?.name ?? 'Campaign'} onClose={onClose}>
      {error && <ErrorState error={error} />}
      {!data ? <Loading /> : (
        <div className="stack" style={{ gap: 18 }}>
          <div className="row"><Status value={c!.status} /><span className="badge plain">{c!.delivery.replace('_', '-')}</span></div>
          <section className="stack">
            <h3>Creatives</h3>
            {data.creatives.map((cr) => <div key={cr.id} className="row" style={{ justifyContent: 'space-between' }}><span>{cr.assetName} · {cr.durationMs / 1000}s</span><Status value={cr.status} /></div>)}
            {['draft', 'rejected'].includes(c!.status) && can(org, 'campaigns.write') && <button className="btn btn-sm" onClick={() => setAdding(true)}><Icon name="plus" /> Add creative</button>}
          </section>
          <section className="stack">
            <h3>Inventory check</h3>
            <Status value={data.inventory.satisfiable ? 'ok' : 'blocked'} label={data.inventory.satisfiable ? 'Fits available airtime' : 'Cannot be guaranteed'} />
            {data.inventory.screens.map((s) => (
              <div key={s.screenName} className="subtle">
                <strong style={{ color: 'var(--text)' }}>{s.screenName}</strong>: {Math.round(s.deliverableSeconds / 60)} min deliverable of {Math.round(s.availableSeconds / 60)} min unsold
                {s.reasons.map((r) => <div key={r} style={{ color: 'var(--danger)' }}>⛔ {r}</div>)}
              </div>
            ))}
          </section>
          {delivery.data && (
            <section className="stack">
              <h3>Delivery (player-reported)</h3>
              <dl className="kv">
                <dt>Started</dt><dd>{delivery.data.observed.started}</dd><dt>Completed</dt><dd>{delivery.data.observed.completed}</dd>
                <dt>Partial</dt><dd>{delivery.data.observed.partial}</dd><dt>Played</dt><dd>{Math.round(delivery.data.deliveredSeconds / 60)} min</dd>
              </dl>
              <p className="subtle">{delivery.data.finalizationNote}</p>
            </section>
          )}
          <div className="row">
            {['draft', 'rejected'].includes(c!.status) && can(org, 'campaigns.write') && <button className="btn btn-primary" onClick={() => void act('/submit', {}, 'Submitted for approval')}>Submit for approval</button>}
            {c!.status === 'submitted' && can(org, 'campaigns.approve') && (
              <>
                <button className="btn btn-primary" onClick={() => void act('/review', { decision: 'approve' }, 'Approved. Publish to deliver it.')}>Approve</button>
                {!data.inventory.satisfiable && <button className="btn" onClick={() => void act('/review', { decision: 'approve', acceptNonGuaranteed: true }, 'Approved as non-guaranteed')}>Approve as non-guaranteed</button>}
                <button className="btn btn-danger" onClick={() => void act('/review', { decision: 'reject', reason: 'Changes requested' }, 'Rejected')}>Reject</button>
              </>
            )}
          </div>
          <section className="stack"><h3>History</h3>{data.history.map((h, i) => <span key={i} className="subtle">rev {h.revision}: {h.from} → {h.to}{h.note ? ` · ${h.note}` : ''} · {new Date(h.at).toLocaleString()}</span>)}</section>
          {adding && (
            <Sheet title="Add creative" onClose={() => setAdding(false)} wide>
              <div className="grid grid-cards">
                {assets.data?.assets.filter((a) => a.kind === 'image' || a.kind === 'video').map((a) => (
                  <button key={a.id} className="card" style={{ padding: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit' }} onClick={() => { setAdding(false); void act('/creatives', { assetId: a.id }, 'Creative added'); }}>
                    <div className="thumb" style={{ borderRadius: '16px 16px 0 0' }}>{a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" /> : null}</div>
                    <div style={{ padding: 12 }}>{a.name}</div>
                  </button>
                ))}
              </div>
            </Sheet>
          )}
        </div>
      )}
    </Inspector>
  );
}
